import React, { useState, useMemo } from 'react';
import {
  Target,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  ExternalLink,
  CheckCircle,
  Circle,
  Play,
  Link as LinkIcon,
} from 'lucide-react';
import { useBoardSprints, useSprintIssues } from '../../hooks/useJira';
import {
  useSprintGoals,
  useCreateGoal,
  useUpdateGoal,
  useDeleteGoal,
  useAddStoryToGoal,
  useRemoveStoryFromGoal,
  calculateGoalProgress,
} from '../../hooks/useSprintGoals';

// Board configuration
const BOARDS = [
  { id: 899, name: 'Platform', key: 'PLT' },
  { id: 898, name: 'Client Portal', key: 'CPT' },
];

// Status category colors
const STATUS_COLORS = {
  done: 'bg-green-100 text-green-700 border-green-200',
  indeterminate: 'bg-blue-100 text-blue-700 border-blue-200',
  new: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_ICONS = {
  done: CheckCircle,
  indeterminate: Play,
  new: Circle,
};

function ProgressBar({ percentage, size = 'md' }) {
  const heights = { sm: 'h-1.5', md: 'h-2', lg: 'h-3' };
  const color = percentage === 100 ? 'bg-green-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-blue-500';

  return (
    <div className={`w-full bg-gray-200 rounded-full ${heights[size]}`}>
      <div
        className={`${color} ${heights[size]} rounded-full transition-all duration-300`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

function StoryChip({ issue, onRemove, showRemove = true }) {
  const StatusIcon = STATUS_ICONS[issue.statusCategory] || Circle;
  const colorClass = STATUS_COLORS[issue.statusCategory] || STATUS_COLORS.new;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs ${colorClass}`}>
      <StatusIcon className="w-3 h-3" />
      <a
        href={`https://sunnyrewards.atlassian.net/browse/${issue.key}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium hover:underline"
      >
        {issue.key}
      </a>
      {issue.storyPoints > 0 && (
        <span className="text-[10px] opacity-70">({issue.storyPoints}pt)</span>
      )}
      {showRemove && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(issue.key);
          }}
          className="ml-0.5 hover:text-red-600"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function GoalCard({ goal, sprintIssues, boardId, sprintId, onEdit }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddStory, setShowAddStory] = useState(false);

  const deleteGoal = useDeleteGoal(boardId, sprintId);
  const removeStory = useRemoveStoryFromGoal(boardId, sprintId);
  const addStory = useAddStoryToGoal(boardId, sprintId);

  const progress = calculateGoalProgress(goal, sprintIssues);

  // Get unmapped stories from sprint
  const unmappedStories = useMemo(() => {
    if (!sprintIssues) return [];
    const mappedKeys = new Set(goal.stories || []);
    return sprintIssues.filter((issue) => !mappedKeys.has(issue.key));
  }, [sprintIssues, goal.stories]);

  const handleRemoveStory = (issueKey) => {
    removeStory.mutate({ goalId: goal.goal_id, issueKey });
  };

  const handleAddStory = (issueKey) => {
    addStory.mutate({ goalId: goal.goal_id, issueKey });
    setShowAddStory(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete goal "${goal.title}"? This will also remove all story mappings.`)) {
      deleteGoal.mutate(goal.goal_id);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <h4 className="font-medium text-gray-900 truncate">{goal.title}</h4>
            </div>
            {goal.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{goal.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(goal)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              title="Edit goal"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
              title="Delete goal"
              disabled={deleteGoal.isPending}
            >
              {deleteGoal.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>
              {progress.done}/{progress.total} stories done
            </span>
            <span className="font-medium">
              {progress.percentage}%
              {progress.percentage === 100 && <CheckCircle className="w-3 h-3 inline ml-1 text-green-500" />}
            </span>
          </div>
          <ProgressBar percentage={progress.percentage} />
        </div>
      </div>

      {/* Stories */}
      <div className="p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-2"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
          Mapped Stories ({progress.total})
        </button>

        {isExpanded && (
          <div className="space-y-2">
            {progress.issues.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {progress.issues.map((issue) => (
                  <StoryChip
                    key={issue.key}
                    issue={issue}
                    onRemove={handleRemoveStory}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No stories mapped yet</p>
            )}

            {/* Add Story */}
            <div className="pt-2">
              {showAddStory ? (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Add Story</span>
                    <button
                      onClick={() => setShowAddStory(false)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {unmappedStories.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {unmappedStories.map((issue) => (
                        <button
                          key={issue.key}
                          onClick={() => handleAddStory(issue.key)}
                          disabled={addStory.isPending}
                          className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-white rounded border border-transparent hover:border-gray-200"
                        >
                          <span className="font-medium text-gray-900">{issue.key}</span>
                          <span className="text-gray-500 truncate flex-1">{issue.summary}</span>
                          {issue.storyPoints > 0 && (
                            <span className="text-xs text-gray-400">{issue.storyPoints}pt</span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">All stories are mapped</p>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAddStory(true)}
                  className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Story
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddGoalForm({ boardId, sprintId, onCancel }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const createGoal = useCreateGoal();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    createGoal.mutate(
      { boardId, sprintId, title: title.trim(), description: description.trim() || null },
      { onSuccess: () => onCancel() }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Goal Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter goal title..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of what this goal aims to achieve..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim() || createGoal.isPending}
            className="px-4 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {createGoal.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create Goal
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

function EditGoalModal({ goal, boardId, sprintId, onClose }) {
  const [title, setTitle] = useState(goal.title);
  const [description, setDescription] = useState(goal.description || '');

  const updateGoal = useUpdateGoal(boardId, sprintId);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    updateGoal.mutate(
      { goalId: goal.goal_id, data: { title: title.trim(), description: description.trim() || null } },
      { onSuccess: onClose }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Edit Goal</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || updateGoal.isPending}
              className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
            >
              {updateGoal.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UnmappedStoriesSection({ sprintIssues, goals, boardId, sprintId }) {
  const addStory = useAddStoryToGoal(boardId, sprintId);
  const [searchTerm, setSearchTerm] = useState('');

  // Get all mapped issue keys across all goals
  const mappedKeys = useMemo(() => {
    const keys = new Set();
    goals?.forEach((goal) => {
      goal.stories?.forEach((key) => keys.add(key));
    });
    return keys;
  }, [goals]);

  // Filter unmapped stories
  const unmappedStories = useMemo(() => {
    if (!sprintIssues) return [];
    return sprintIssues.filter((issue) => !mappedKeys.has(issue.key));
  }, [sprintIssues, mappedKeys]);

  // Filter by search term
  const filteredStories = useMemo(() => {
    if (!searchTerm.trim()) return unmappedStories;
    const term = searchTerm.toLowerCase();
    return unmappedStories.filter(
      (issue) =>
        issue.key.toLowerCase().includes(term) ||
        issue.summary.toLowerCase().includes(term)
    );
  }, [unmappedStories, searchTerm]);

  const handleAddToGoal = (issueKey, goalId) => {
    addStory.mutate({ goalId, issueKey });
  };

  if (unmappedStories.length === 0) return null;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-700 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          Unmapped Stories ({unmappedStories.length})
        </h4>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search stories..."
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 w-48"
        />
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {filteredStories.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-2">No stories match your search</p>
        ) : (
          filteredStories.map((issue) => {
            const StatusIcon = STATUS_ICONS[issue.statusCategory] || Circle;
            return (
              <div
                key={issue.key}
                className="flex items-center gap-3 p-2 bg-white rounded border border-gray-100 hover:border-gray-200"
              >
                <StatusIcon className={`w-4 h-4 ${
                  issue.statusCategory === 'done' ? 'text-green-500' :
                  issue.statusCategory === 'indeterminate' ? 'text-blue-500' : 'text-gray-400'
                }`} />
                <a
                  href={`https://sunnyrewards.atlassian.net/browse/${issue.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-sm text-gray-900 hover:text-amber-600"
                >
                  {issue.key}
                </a>
                <span className="text-sm text-gray-500 truncate flex-1">{issue.summary}</span>
                {issue.storyPoints > 0 && (
                  <span className="text-xs text-gray-400">{issue.storyPoints}pt</span>
                )}
                {goals?.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => e.target.value && handleAddToGoal(issue.key, parseInt(e.target.value))}
                    className="text-xs px-2 py-1 border border-gray-200 rounded hover:border-amber-300 focus:ring-1 focus:ring-amber-500"
                  >
                    <option value="">Add to goal...</option>
                    {goals.map((goal) => (
                      <option key={goal.goal_id} value={goal.goal_id}>
                        {goal.title}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function SprintGoals() {
  const [selectedBoard, setSelectedBoard] = useState(BOARDS[0]);
  const [selectedSprintId, setSelectedSprintId] = useState(null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  // Fetch sprints for selected board
  const {
    data: sprintsData,
    isLoading: sprintsLoading,
  } = useBoardSprints(selectedBoard.id, 'active,closed');

  const sprints = sprintsData?.data || [];

  // Auto-select active sprint
  React.useEffect(() => {
    if (sprints.length > 0 && !selectedSprintId) {
      const activeSprint = sprints.find((s) => s.state === 'active');
      setSelectedSprintId(activeSprint?.id || sprints[0]?.id);
    }
  }, [sprints, selectedSprintId]);

  // Reset sprint selection when board changes
  React.useEffect(() => {
    setSelectedSprintId(null);
  }, [selectedBoard.id]);

  // Fetch goals for selected sprint
  const {
    data: goalsData,
    isLoading: goalsLoading,
    isError: goalsError,
  } = useSprintGoals(selectedBoard.id, selectedSprintId);

  const goals = goalsData?.data || [];

  // Fetch sprint issues for progress calculation
  const {
    data: issuesData,
    isLoading: issuesLoading,
  } = useSprintIssues(selectedSprintId);

  const sprintIssues = issuesData?.data || [];

  const selectedSprint = sprints.find((s) => s.id === selectedSprintId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-amber-500" />
            Sprint Goals
          </h2>
          <p className="text-gray-500 mt-1 text-sm">
            Define and track high-level goals for your sprints
          </p>
        </div>
      </div>

      {/* Board & Sprint Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Board Selector */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Board</label>
            <div className="flex gap-2">
              {BOARDS.map((board) => (
                <button
                  key={board.id}
                  onClick={() => setSelectedBoard(board)}
                  className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                    selectedBoard.id === board.id
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {board.name}
                </button>
              ))}
            </div>
          </div>

          {/* Sprint Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Sprint</label>
            {sprintsLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading sprints...
              </div>
            ) : (
              <select
                value={selectedSprintId || ''}
                onChange={(e) => setSelectedSprintId(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Select a sprint...</option>
                {sprints.map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>
                    {sprint.name} {sprint.state === 'active' ? '(Active)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Sprint Link */}
          {selectedSprint && (
            <a
              href={`https://sunnyrewards.atlassian.net/jira/software/projects/${selectedBoard.key}/boards/${selectedBoard.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-amber-600"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Jira
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      {!selectedSprintId ? (
        <div className="text-center py-12 text-gray-500">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Select a sprint to manage goals</p>
        </div>
      ) : goalsLoading || issuesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
          <span className="ml-2 text-gray-500">Loading goals...</span>
        </div>
      ) : goalsError ? (
        <div className="flex items-center justify-center py-12 text-red-500">
          <AlertCircle className="w-5 h-5 mr-2" />
          Failed to load goals
        </div>
      ) : (
        <div className="space-y-4">
          {/* Goals List */}
          {goals.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {goals.map((goal) => (
                <GoalCard
                  key={goal.goal_id}
                  goal={goal}
                  sprintIssues={sprintIssues}
                  boardId={selectedBoard.id}
                  sprintId={selectedSprintId}
                  onEdit={setEditingGoal}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <Target className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No goals defined for this sprint yet</p>
              <p className="text-sm text-gray-400 mt-1">Click &quot;Add Goal&quot; to get started</p>
            </div>
          )}

          {/* Add Goal Form or Button */}
          {showAddGoal ? (
            <AddGoalForm
              boardId={selectedBoard.id}
              sprintId={selectedSprintId}
              onCancel={() => setShowAddGoal(false)}
            />
          ) : (
            <button
              onClick={() => setShowAddGoal(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-amber-400 hover:text-amber-600 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Goal
            </button>
          )}

          {/* Unmapped Stories */}
          <UnmappedStoriesSection
            sprintIssues={sprintIssues}
            goals={goals}
            boardId={selectedBoard.id}
            sprintId={selectedSprintId}
          />
        </div>
      )}

      {/* Edit Goal Modal */}
      {editingGoal && (
        <EditGoalModal
          goal={editingGoal}
          boardId={selectedBoard.id}
          sprintId={selectedSprintId}
          onClose={() => setEditingGoal(null)}
        />
      )}
    </div>
  );
}
